import Vue from "vue";
import {
    FetchState,
    EbtoState,
    EbtoRequest,
    EbtoResponse,
    EbtoStep,
    EbtoCampaign,
    CampaignDecision,
    EbtoUserFinalizeRequest,
    EbtoEmailRequest,
    EbtoVerifyUserRequest
} from "@/shared/types";
import * as userService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: EbtoState = {
    userSubmissionFetchState: FetchState.Uninitialized,
    campaignFetchState: FetchState.Uninitialized,
    esignFetchState: FetchState.Uninitialized,
    offerFetchState: FetchState.Uninitialized,
    claimUserFetchState: FetchState.Uninitialized,
    confirmationEmailFetchState: FetchState.Uninitialized,
    verificationEmailFetchState: FetchState.Uninitialized,
    campaignId: null,
    userFlowComplete: false,
    isValidCampaign: false,
    currentEbtoStep: EbtoStep.Claim,
    campaign: {
        isAdminFeeWaived: false,
        bonusAmount: 0,
        id: 0,
        campaignId: "",
        dateCutOff: null,
        dateElectionEnd: null,
        dateDistributionsEnd: null,
        dateElectionStart: null,
        datePricingOffer: null,
        dateTransfer: null,
        newCustodianEffectiveDate: null,
        newCustodian: "",
        employerName: ""
    },
    claimUser: {
        email: "",
        phone: "",
        userId: 0,
        decision: CampaignDecision.NotDecided,
        userLocked: false,
        lockEndTime: null
    },
    hasEsigned: false,
    autoLogout: false,
    isInvalidUser: false,
    isVerified: false,
    isVerificationLocked: false
};

export const useEbtoStore = defineStore("ebto", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("ebto"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isCampaignValidationLoaded: state => state.campaignFetchState === FetchState.Loaded,
        offerIsLoaded: state => state.offerFetchState === FetchState.Loaded,
        isFeeWaived: state => state.campaign.isAdminFeeWaived,
        incentiveAmount: state => state.campaign.bonusAmount,
        isClaimUserLoaded: state => state.claimUserFetchState === FetchState.Loaded,
        verifiedUser: state => state.claimUser,
        isUserFlowComplete: state => state.userFlowComplete,
        electionEnd: state => state.campaign.dateElectionEnd,
    },
    actions: {
        setUserSubmissionFetchState (fetchState: FetchState) {
            this.userSubmissionFetchState = fetchState;
        },
        setCampaignFetchState (fetchState: FetchState) {
            this.campaignFetchState = fetchState;
        },
        setEsignFetchState (fetchState: FetchState) {
            this.esignFetchState = fetchState;
        },
        setOfferFetchState (fetchState: FetchState) {
            this.offerFetchState = fetchState;
        },
        setClaimUserFetchState (fetchstate: FetchState) {
            this.claimUserFetchState = fetchstate;
        },
        setConfirmationEmailFetchState (fetchstate: FetchState) {
            this.confirmationEmailFetchState = fetchstate;
        },
        setVerificationEmailFetchState (fetchstate: FetchState) {
            this.verificationEmailFetchState = fetchstate;
        },
        setCampaignId (campaignId: string) {
            this.campaignId = campaignId;
        },
        setCampaignValidationResult (validationResult: boolean) {
            this.isValidCampaign = validationResult;
        },
        setCurrentEbtoStep (currentEbtoStep: EbtoStep) {
            this.currentEbtoStep = currentEbtoStep;
        },
        setEbtoCampaign (campaign: EbtoCampaign) {
            this.campaign = { ...campaign };
        },
        setHasEsigned (value: boolean) {
            this.hasEsigned = value;
        },
        setClaimUser (claimUser: EbtoResponse) {
            this.claimUser = { ...claimUser };
        },
        setUserFlowComplete (isComplete: boolean) {
            this.userFlowComplete = isComplete;
        },
        setAutoLogout (value: boolean) {
            this.autoLogout = value;
        },
        setIsInvalidUser (value: boolean) {
            this.isInvalidUser = value;
        },
        setVerified (value: boolean) {
            this.isVerified = value;
        },
        setIsVerificationLocked () {
            this.isVerificationLocked = true;
        },
        setLockOnVerifiedUser (lockEndTime: string): void {
            this.claimUser.userLocked = true;
            this.claimUser.lockEndTime = lockEndTime;
        },
        resetState (): void {
            this.$reset();
        },
        beginAutoLogout () {
            this.setAutoLogout(true);
        },
        ebtoGetCampaign (campaignId: string): Promise<any> {

            if (this.campaignId !== campaignId) {
                this.resetState();
            }

            if (this.campaignFetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            return userService.ebtoGetCampaign(campaignId)
                .then(result => {
                    this.setEbtoCampaign(result.data);
                    this.setCampaignFetchState(FetchState.Loaded);
                    this.setCampaignValidationResult(true);
                    this.setCampaignId(campaignId);
                })
                .catch(err => {
                    this.setCampaignFetchState(FetchState.Loaded);
                    this.setCampaignValidationResult(false);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        claimUserEbtoCampaign (request: EbtoRequest): Promise<any> {
            this.setClaimUserFetchState(FetchState.Loading);

            return userService.ebtoClaimUserCampaign(request)
                .then(result => {
                    this.setClaimUser(result.data);
                    this.setClaimUserFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setClaimUserFetchState(FetchState.Loaded);
                    this.setIsInvalidUser(true);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        sendConfirmationEmail (request: EbtoEmailRequest): Promise<any> {
            this.setConfirmationEmailFetchState(FetchState.Loading);

            return userService.ebtoSendConfirmationEmailStay(request)
                .then(result => {
                    this.setConfirmationEmailFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setConfirmationEmailFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        esignEbtoDocument (): Promise<any> {
            this.setEsignFetchState(FetchState.Loading);


            return;
        },
        finalizeEbtoStayOffer (request: EbtoUserFinalizeRequest): Promise<any> {
            this.setOfferFetchState(FetchState.Loading);

            return userService.ebtoFinalizeOffer(this.campaignId, request)
                .then(result => {
                    this.setClaimUser(result.data);
                    this.setUserFlowComplete(true);
                    this.setOfferFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setOfferFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        verifyUser (verificationRequest: any) {
            if (this.verificationEmailFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            const request: EbtoVerifyUserRequest = {
                userId: this.claimUser.userId,
                verificationCode: verificationRequest.verificationCode,
                verificationType: verificationRequest.verificationType
            };

            return userService.ebtoVerifyUser(this.campaignId, request)
                .then(result => {
                    this.setVerificationEmailFetchState(FetchState.Loaded);
                    this.setVerified(true);
                })
                .catch(err => {
                    if (err.response.status === 423) {
                        this.setIsVerificationLocked(true);
                    }
                    this.setVerificationEmailFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        resendVerificationCode (verificationType: number) {
            return userService.ebtoResendVerificationCode(this.campaignId, `${this.claimUser.userId}`, verificationType);
        }
    }
});
